import { LogSorterColumnPlacement } from 'shared/logSorter/domain/logSorterBinLayout';
import { LogSorterNumberSequence } from 'shared/logSorter/domain/logSorterNumberSequence';
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import type { Coordinate } from 'shared/map/domain/coordinate';
import type { LogSorterResponse } from '@/logSorter/api/common/logSorterResponse';
import { migrateConfiguration, type LogSorterConfiguration } from '@/logSorter/domain/logSorterConfiguration';
import {
  createPreviewRectangle,
  geoJsonRectangle,
  type PreviewRectangle,
  type RectangleGeofence,
} from 'shared/map/domain/rectangle';
import { destination, point, type Coord, type Units } from '@turf/turf';
import { Anchor } from 'shared/map/domain/anchor';
import { geoJsonStyle } from '../../../../shared/map/domain/style';
import type { LogSorterBin } from '@/logSorter/api/common/logSorterBin';
import { useLanguageStore } from 'shared/translations/stores/languageStore';

const turfOptions: { units: Units } = { units: 'meters' };

export const useSelectedLogSorterStore = defineStore('selectedLogSorter', () => {
  const languageStore = useLanguageStore();
  const { translations } = storeToRefs(languageStore);
  const operation = ref<'none' | 'create' | 'edit'>('none');

  const currentLogSorter = ref<LogSorterResponse>();

  const currentLogSorterConfiguration = computed(() => {
    if (currentLogSorter.value === undefined) return;
    const logSorter = currentLogSorter.value;
    if (logSorter.configuration) {
      return JSON.parse(logSorter.configuration) as LogSorterConfiguration;
    }

    return undefined;
  });

  const logSorterId = computed(() => {
    return currentLogSorter.value?.id;
  });

  const name = ref<string>();

  const nameChanged = computed(() => {
    if (currentLogSorter.value === undefined) return false;
    return currentLogSorter.value.name !== name.value;
  });

  const nameInvalid = computed(() => {
    if (name.value === '' || name.value === undefined || name.value === null) {
      return translations.value.error.nameIsRequired;
    }

    return undefined;
  });

  const intakes = ref<string[]>([]);

  const intakesChanged = computed(() => {
    if (currentLogSorter.value === undefined) return false;
    const prev = new Set(currentLogSorter.value.intakes);
    const current = new Set(intakes.value);

    if (prev.size !== current.size) return true;

    for (const id of prev) {
      if (!current.has(id)) return true;
    }

    for (const id of current) {
      if (!prev.has(id)) return true;
    }

    return false;
  });

  const addLogWhenSorted = ref<boolean>(false);

  const addLogWhenSortedChanged = computed(() => {
    if (currentLogSorter.value === undefined) return false;
    return currentLogSorter.value.addLogWhenSorted !== addLogWhenSorted.value;
  });

  const longitude = ref<number>();

  const longitudeChanged = computed(() => {
    if (currentLogSorter.value === undefined) return false;
    return currentLogSorterConfiguration.value?.longitude !== longitude.value;
  });

  const longitudeInvalid = computed(() => {
    if (longitude.value === undefined || longitude.value === null) {
      return translations.value.error.required;
    }

    return undefined;
  });

  const latitude = ref<number>();

  const latitudeChanged = computed(() => {
    if (currentLogSorter.value === undefined) return false;
    return currentLogSorterConfiguration.value?.latitude !== latitude.value;
  });

  const latitudeInvalid = computed(() => {
    if (latitude.value === undefined || latitude.value === null) {
      return translations.value.error.required;
    }

    return undefined;
  });

  const coordinate = computed<Coordinate | undefined>(() => {
    if (
      longitude.value === undefined ||
      longitude.value === null ||
      latitude.value === undefined ||
      latitude.value === null
    )
      return;
    return {
      longitude: longitude.value,
      latitude: latitude.value,
    };
  });

  const heading = ref<number>(0);

  const headingChanged = computed(() => {
    if (currentLogSorter.value === undefined) return false;
    return currentLogSorterConfiguration.value?.direction !== heading.value;
  });

  const numberSequence = ref<LogSorterNumberSequence>(LogSorterNumberSequence.Sequential);

  const numberSequenceChanged = computed(() => {
    if (currentLogSorter.value === undefined) return false;

    const code = currentLogSorterConfiguration.value?.numberSequence;

    switch (numberSequence.value) {
      case LogSorterNumberSequence.Sequential:
        return code !== 'Sequential';
      case LogSorterNumberSequence.SequentialMirrored:
        return code !== 'SequentialMirrored';
      case LogSorterNumberSequence.Alternating:
        return code !== 'Alternating';
      case LogSorterNumberSequence.AlternatingMirrored:
        return code !== 'AlternatingMirrored';
      default:
        return false;
    }
  });

  const numberOfBinsCol1 = ref<number>(4);

  const numberOfBinsCol1Changed = computed(() => {
    if (currentLogSorter.value === undefined) return false;
    return currentLogSorterConfiguration.value?.numberOfBinsCol1 !== numberOfBinsCol1.value;
  });

  const numberOfBinsCol2 = ref<number>(4);

  const numberOfBinsCol2Changed = computed(() => {
    if (currentLogSorter.value === undefined) return false;
    return currentLogSorterConfiguration.value?.numberOfBinsCol2 !== numberOfBinsCol2.value;
  });

  const binLayout = ref<LogSorterColumnPlacement>(LogSorterColumnPlacement.Right);

  const binLayoutChanged = computed(() => {
    if (currentLogSorter.value === undefined) return false;

    const code = currentLogSorterConfiguration.value?.columnPlacement;

    switch (binLayout.value) {
      case LogSorterColumnPlacement.Left:
        return code !== 'Left';
      case LogSorterColumnPlacement.Right:
        return code !== 'Right';
      default:
        return false;
    }
  });

  const binDefaultWidth = ref<number>(10);

  const binWidthChanged = computed(() => {
    if (currentLogSorter.value === undefined) return false;
    return currentLogSorterConfiguration.value?.binDefaultWidth !== binDefaultWidth.value;
  });

  const binDefaultLength = ref<number>(10);

  const binLengthChanged = computed(() => {
    if (currentLogSorter.value === undefined) return false;
    return currentLogSorterConfiguration.value?.binDefaultWidth !== binDefaultLength.value;
  });

  const columnGap = ref<number>(5);

  const columnGapChanged = computed(() => {
    if (currentLogSorter.value === undefined) return false;
    return currentLogSorterConfiguration.value?.distanceX !== columnGap.value;
  });

  const rowGap = ref<number>(5);

  const rowGapChanged = computed(() => {
    if (currentLogSorter.value === undefined) return false;
    return currentLogSorterConfiguration.value?.distanceY !== rowGap.value;
  });

  const geofenceTop = ref<number>(0);

  const geofenceTopChanged = computed(() => {
    if (currentLogSorter.value === undefined) return false;
    return currentLogSorterConfiguration.value?.geofenceTop !== geofenceTop.value;
  });

  const geofenceBottom = ref<number>(0);

  const geofenceBottomChanged = computed(() => {
    if (currentLogSorter.value === undefined) return false;
    return currentLogSorterConfiguration.value?.geofenceBottom !== geofenceBottom.value;
  });

  const geofenceInside = ref<number>(0);

  const geofenceInsideChanged = computed(() => {
    if (currentLogSorter.value === undefined) return false;
    return currentLogSorterConfiguration.value?.geofenceInside !== geofenceInside.value;
  });

  const geofenceOutside = ref<number>(0);

  const geofenceOutsideChanged = computed(() => {
    if (currentLogSorter.value === undefined) return false;
    return currentLogSorterConfiguration.value?.geofenceOutside !== geofenceOutside.value;
  });

  const changed = computed(() => {
    if (geofenceInsideChanged.value || geofenceOutsideChanged.value) {
      return true;
    }

    return (
      nameChanged.value ||
      longitudeChanged.value ||
      latitudeChanged.value ||
      headingChanged.value ||
      numberSequenceChanged.value ||
      numberOfBinsCol1Changed.value ||
      numberOfBinsCol2Changed.value ||
      binLayoutChanged.value ||
      binWidthChanged.value ||
      binLengthChanged.value ||
      columnGapChanged.value ||
      rowGapChanged.value ||
      geofenceTopChanged.value ||
      geofenceBottomChanged.value ||
      bins.value.some((b) => Object.values(b.hasChanged).some(Boolean)) ||
      intakesChanged.value
    );
  });

  const storageAreaIds = computed(() => {
    const logSorter = currentLogSorter.value;
    const ids = new Set<string>();
    if (logSorter === undefined) return ids;
    for (const bin of logSorter.bins) {
      ids.add(bin.storageAreaId);
    }
    return ids;
  });

  const invalid = computed(() => {
    if (nameInvalid.value) return true;
    if (longitudeInvalid.value) return true;
    if (latitudeInvalid.value) return true;
    return false;
  });

  const configuration = computed<LogSorterConfiguration>(() => {
    const config: LogSorterConfiguration = {
      binDefaultLength: binDefaultLength.value,
      binDefaultWidth: binDefaultWidth.value,
      direction: heading.value,
      distanceX: columnGap.value,
      distanceY: rowGap.value,
      geofenceBottom: geofenceBottom.value,
      geofenceInside: geofenceInside.value,
      geofenceOutside: geofenceOutside.value,
      geofenceTop: geofenceTop.value,
      latitude: latitude.value,
      longitude: longitude.value,
      numberOfBinsCol1: numberOfBinsCol1.value,
      numberOfBinsCol2: numberOfBinsCol2.value,
      columnPlacement: 'Right',
      numberSequence: 'Sequential',
    };

    switch (binLayout.value) {
      case LogSorterColumnPlacement.Left:
        config.columnPlacement = 'Left';
        break;
      case LogSorterColumnPlacement.Right:
        config.columnPlacement = 'Right';
        break;
    }

    switch (numberSequence.value) {
      case LogSorterNumberSequence.Sequential:
        config.numberSequence = 'Sequential';
        break;
      case LogSorterNumberSequence.SequentialMirrored:
        config.numberSequence = 'SequentialMirrored';
        break;
      case LogSorterNumberSequence.Alternating:
        config.numberSequence = 'Alternating';
        break;
      case LogSorterNumberSequence.AlternatingMirrored:
        config.numberSequence = 'AlternatingMirrored';
        break;
    }

    return config;
  });

  const binNumberMap = computed(() => {
    const map = new Map<number, LogSorterBin>();
    const logSorter = currentLogSorter.value;
    if (logSorter) {
      for (const bin of logSorter.bins) {
        map.set(bin.binNumber, bin);
      }
    }
    return map;
  });

  const binSettingsMap = new Map<number, BinSettings>();

  const bins = ref<Bin[]>([]);

  watch(
    [
      coordinate,
      numberOfBinsCol1,
      numberOfBinsCol2,
      numberSequence,
      binLayout,
      heading,
      binDefaultWidth,
      binDefaultLength,
      geofenceTop,
      geofenceBottom,
      geofenceInside,
      geofenceOutside,
    ],
    () => {
      bins.value = generateBins(true);
    },
  );

  const deleteStorageAreaIds = computed<Set<string>>(() => {
    const previousBins = currentLogSorter.value?.bins;
    const deleteBinIds = new Set<string>();
    if (!previousBins) return deleteBinIds;
    const currentBinIds = new Set<string>();
    for (const bin of bins.value) {
      if (!bin.storageAreaId) continue;
      currentBinIds.add(bin.storageAreaId);
    }

    for (const bin of previousBins) {
      if (!currentBinIds.has(bin.storageAreaId)) {
        deleteBinIds.add(bin.storageAreaId);
      }
    }

    return deleteBinIds;
  });

  function onBinChanged(update: boolean) {
    bins.value = generateBins(update);
  }

  function generateBins(update: boolean) {

    if (update) {
      for (const bin of bins.value) {
        binSettingsMap.set(bin.number, {
          identifier: bin.identifier,
          width: bin.width,
          length: bin.length,
          offsetX: bin.offsetX,
          offsetY: bin.offsetY,
        });
      }
    } else {
      binSettingsMap.clear();
    }

    const { col1, col2 } = getBinNumbers(
      numberSequence.value,
      numberOfBinsCol1.value,
      numberOfBinsCol2.value,
    );

    const bins1 = generateBinColumn(col1, 0);
    const bins2 = generateBinColumn(col2, binDefaultWidth.value + columnGap.value);

    return [...bins1, ...bins2].sort((a, b) => a.number - b.number);
  }

function getBinNumbers(
  mode: LogSorterNumberSequence,
  col1Count: number = 0,
  col2Count: number = 0,
) {
  const total = col1Count + col2Count;

  const all = Array.from({ length: total }, (_, i) => i + 1);

  let col1: number[] = [];
  let col2: number[] = [];

  switch (mode) {
    case LogSorterNumberSequence.Sequential:
    case LogSorterNumberSequence.SequentialMirrored: {
      col1 = all.slice(0, col1Count);
      col2 = all.slice(col1Count);
      break;
    }

    case LogSorterNumberSequence.Alternating:
    case LogSorterNumberSequence.AlternatingMirrored: {
      for (let i = 0; i < all.length; i++) {
        if (i % 2 === 0) {
          if (col1.length < col1Count) col1.push(all[i]!);
          else col2.push(all[i]!);
        } else {
          if (col2.length < col2Count) col2.push(all[i]!);
          else col1.push(all[i]!);
        }
      }
      break;
    }
  }

  const mirrored =
    mode === LogSorterNumberSequence.SequentialMirrored ||
    mode === LogSorterNumberSequence.AlternatingMirrored;

  if (mirrored) {
    col1.reverse();
    col2.reverse();
  }

  return { col1, col2 };
}

  function generateBinColumn(binNumbers: number[], offsetX: number): Bin[] {
    const result: Bin[] = [];

    if (coordinate.value === undefined) return result;
    if (numberOfBinsCol1.value <= 0 && numberOfBinsCol2.value <= 0) return result;

    let accumulatedLength = 0;
    const logSorterPoint = point([coordinate.value.longitude, coordinate.value.latitude]);
    const binMap = binNumberMap.value;

    const startingPoint = destination(
      logSorterPoint,
      offsetX,
      heading.value + (binLayout.value === LogSorterColumnPlacement.Left ? -90 : +90),
      turfOptions,
    );

    for (let i = 0; i < binNumbers.length; i++) {
      const binNumber = binNumbers[i];
      if (!binNumber) continue;
      const settings = binSettingsMap.get(binNumber);

      const binWidth = settings?.width ?? binDefaultWidth.value;
      const binLength = settings?.length ?? binDefaultLength.value;
      const binOffsetX = settings?.offsetX ?? 0;
      const binOffsetY = settings?.offsetY ?? 0;

      let binPoint = destination(startingPoint, accumulatedLength, heading.value, turfOptions);

      if (binOffsetY !== 0) {
        binPoint = destination(binPoint, binOffsetY, heading.value, turfOptions);
      }

      if (binOffsetX !== 0) {
        binPoint = destination(binPoint, binOffsetX, heading.value + 90, turfOptions);
      }

      accumulatedLength += binLength + binOffsetY + rowGap.value;
      const binGeofence = calculateBinGeofence();

      const binShape = createPreviewRectangle({
        anchor: Anchor.BottomLeft,
        coordinate: {
          longitude: binPoint.geometry.coordinates[0]!,
          latitude: binPoint.geometry.coordinates[1]!,
        },
        heading: heading.value,
        width: binWidth,
        length: binLength,
        geofence: binGeofence,
      });

      const shapeStyle = geoJsonStyle(binShape.shape);

      shapeStyle.color = '#fef3c7';
      shapeStyle.outlineColor = '#000000';

      const geofenceStyle = geoJsonStyle(binShape.geofence);

      geofenceStyle.color = '#d8b4fe';
      geofenceStyle.outlineColor = '#000000';

      const properties = geoJsonRectangle(binShape.shape);
      properties.anchor = Anchor.BottomLeft;
      properties.coordinate = coordinate.value;
      properties.geofenceBottom = binGeofence.bottom;
      properties.geofenceLeft = binGeofence.left;
      properties.geofenceRight = binGeofence.right;
      properties.geofenceTop = binGeofence.top;
      properties.heading = heading.value;
      properties.length = binLength;
      properties.width = binWidth;
      properties.label = binNumber.toString();

      const binInfo = binMap.get(binNumber);
      const storageAreaId = binInfo?.storageAreaId;
      const identifier = binSettingsMap.get(binNumber)?.identifier ?? binInfo?.binIdentifier ?? '';

      const savedBin = currentLogSorter.value?.bins.find((bin) => bin.binNumber === binNumber);

      const bin: Bin = {
        name: translations.value.logSorter.drawer.binName(binNumber),
        number: binNumber,
        geoJson: binShape,
        storageAreaId,
        identifier: identifier,
        width: binWidth,
        length: binLength,
        offsetX: binOffsetX,
        offsetY: binOffsetY,
        hasChanged: {
          identifier: identifier !== savedBin?.binIdentifier,
          width: binWidth !== savedBin?.width,
          length: binLength !== savedBin?.length,
          offsetX: binOffsetX !== savedBin?.offsetX,
          offsetY: binOffsetY !== savedBin?.offsetY,
        },
      };

      result.push(bin);

      function calculateBinGeofence(): RectangleGeofence {
        const firstColumn = offsetX == 0;

        const geofence: RectangleGeofence = {
          top: geofenceTop.value,
          bottom: geofenceBottom.value,
          left: geofenceOutside.value,
          right: geofenceInside.value,
        };

        switch (binLayout.value) {
          case LogSorterColumnPlacement.Left:
            geofence.left = firstColumn ? geofenceInside.value : geofenceOutside.value;
            geofence.right = firstColumn ? geofenceOutside.value : geofenceInside.value;
            break;
          case LogSorterColumnPlacement.Right:
            geofence.left = firstColumn ? geofenceOutside.value : geofenceInside.value;
            geofence.right = firstColumn ? geofenceInside.value : geofenceOutside.value;
            break;
        }

        return geofence;
      }
    }
    return result;
  }

  function create(action: CreateLogSorter) {
    currentLogSorter.value = undefined;

    name.value = undefined;
    intakes.value = [];
    longitude.value = action.coordinate.longitude;
    latitude.value = action.coordinate.latitude;

    heading.value = 0;
    numberSequence.value = LogSorterNumberSequence.Sequential;
    numberOfBinsCol1.value = 4;
    numberOfBinsCol2.value = 4;
    binLayout.value = LogSorterColumnPlacement.Right;
    binDefaultWidth.value = 10;
    binDefaultLength.value = 10;
    columnGap.value = 5;
    rowGap.value = 5;

    geofenceTop.value = 0;
    geofenceBottom.value = 0;
    geofenceInside.value = 0;
    geofenceOutside.value = 0;
    operation.value = 'create';

    bins.value = generateBins(false);
  }

  function edit(action: EditLogSorter) {
    const { logSorter } = action;
    currentLogSorter.value = logSorter;

    let configuration: LogSorterConfiguration | null = null;

    if (logSorter.configuration) {
      configuration = migrateConfiguration(JSON.parse(logSorter.configuration));
    }

    binSettingsMap.clear();
    logSorter.bins.forEach((bin) => {
      binSettingsMap.set(bin.binNumber, {
        identifier: bin.binIdentifier,
        width: bin.width,
        length: bin.length,
        offsetX: bin.offsetX,
        offsetY: bin.offsetY,
      });
    });

    name.value = logSorter.name;
    intakes.value = logSorter.intakes;
    longitude.value = configuration?.longitude;
    latitude.value = configuration?.latitude;

    heading.value = configuration?.direction ?? 0;

    switch (configuration?.numberSequence) {
      case 'Sequential':
        numberSequence.value = LogSorterNumberSequence.Sequential;
        break;
      case 'SequentialMirrored':
        numberSequence.value = LogSorterNumberSequence.SequentialMirrored;
        break;
      case 'Alternating':
        numberSequence.value = LogSorterNumberSequence.Alternating;
        break;
      case 'AlternatingMirrored':
        numberSequence.value = LogSorterNumberSequence.AlternatingMirrored;
        break;
      default:
        numberSequence.value = LogSorterNumberSequence.Sequential;
        break;
    }

    numberOfBinsCol1.value = configuration?.numberOfBinsCol1 ?? 8;
    numberOfBinsCol2.value = configuration?.numberOfBinsCol2 ?? 8;

    switch (configuration?.columnPlacement) {
      case 'Left':
        binLayout.value = LogSorterColumnPlacement.Left;
        break;
      case 'Right':
        binLayout.value = LogSorterColumnPlacement.Right;
        break;
      default:
        binLayout.value = LogSorterColumnPlacement.Right;
        break;
    }

    binDefaultWidth.value = configuration?.binDefaultLength ?? 10;
    binDefaultLength.value = configuration?.binDefaultLength ?? 10;
    columnGap.value = configuration?.distanceX ?? 5;
    rowGap.value = configuration?.distanceY ?? 5;

    geofenceTop.value = configuration?.geofenceTop ?? 0;
    geofenceBottom.value = configuration?.geofenceBottom ?? 0;
    geofenceInside.value = configuration?.geofenceInside ?? 0;
    geofenceOutside.value = configuration?.geofenceOutside ?? 0;

    operation.value = 'edit';
    bins.value = generateBins(true);

  }

  function hide() {
    currentLogSorter.value = undefined;
    longitude.value = undefined;
    latitude.value = undefined;
    operation.value = 'none';
  }

  function newCoordinate(coordinate: Coordinate) {
    longitude.value = coordinate.longitude;
    latitude.value = coordinate.latitude;
  }

  return {
    create,
    edit,
    hide,
    newCoordinate,
    onBinChanged,
    operation,
    logSorterId,
    name,
    nameChanged,
    nameInvalid,
    intakes,
    intakesChanged,
    addLogWhenSorted,
    addLogWhenSortedChanged,
    longitude,
    longitudeChanged,
    longitudeInvalid,
    latitude,
    latitudeChanged,
    latitudeInvalid,
    coordinate,
    heading,
    headingChanged,
    numberSequence,
    numberSequenceChanged,
    numberOfBinsCol1,
    numberOfBinsCol1Changed,
    numberOfBinsCol2,
    numberOfBinsCol2Changed,
    binLayout,
    binLayoutChanged,
    binDefaultWidth,
    binWidthChanged,
    binDefaultLength,
    binLengthChanged,
    columnGap,
    columnGapChanged,
    rowGap,
    rowGapChanged,
    geofenceTop,
    geofenceTopChanged,
    geofenceBottom,
    geofenceBottomChanged,
    geofenceInside,
    geofenceInsideChanged,
    geofenceOutside,
    geofenceOutsideChanged,
    changed,
    storageAreaIds,
    configuration,
    bins,
    deleteStorageAreaIds,
    invalid,
  };
});

export interface CreateLogSorter {
  coordinate: Coordinate;
}

export interface EditLogSorter {
  logSorter: LogSorterResponse;
}

export interface Bin {
  name: string;
  number: number;
  storageAreaId?: string;
  geoJson: PreviewRectangle;
  identifier: string;
  width: number;
  length: number;
  offsetY: number;
  offsetX: number;
  hasChanged: BinSettingChanged;
}

type BinSettings = {
  identifier: string;
  width: number;
  length: number;
  offsetX: number;
  offsetY: number;
};

type BinSettingChanged = {
  identifier: boolean;
  width: boolean;
  length: boolean;
  offsetX: boolean;
  offsetY: boolean;
}
